import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  bulkUpdateStudentClassId,
  getClassesBySchoolId,
  getStudentsByClassId
} from "../services/APIClient";
import "./TeacherMoveChildrenModal.css";

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getClassGrade = (classItem) =>
  GRADE_LEVELS.find((grade) => classItem?.[grade.value])?.label ||
  classItem?.grade ||
  classItem?.gradeLevel ||
  "";

const normaliseClass = (classItem) => ({
  ...classItem,
  id: classItem?.id || classItem?._id,
  name: classItem?.name || classItem?.className || "Unnamed class",
  grade: getClassGrade(classItem),
  academicYear: classItem?.academicYear || classItem?.academic_year || ""
});

const normaliseStudent = (student, fallbackClass) => ({
  ...student,
  id: student?.id || student?._id || student?.studentid || student?.studentId,
  name: student?.name || student?.fullName || student?.displayName || "Unnamed child",
  classId: student?.classId || student?.classid || student?.class_id || fallbackClass?.id,
  className: student?.className || student?.class_name || fallbackClass?.name || ""
});

const parseStudentNames = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  const text = String(value || "").trim();

  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const parsedValue = JSON.parse(text);

      if (Array.isArray(parsedValue)) {
        return parsedValue;
      }
    } catch {
      // Keep non-JSON text as entered.
    }
  }

  return text.replace(/,/g, "\n");
};

const normalisePrefill = (prefill) => {
  const rawStudentNames =
    prefill?.studentNames ||
    prefill?.student_names ||
    prefill?.children ||
    prefill?.childNames ||
    prefill?.child_names ||
    prefill?.studentName ||
    prefill?.student_name ||
    "";
  const parsedStudentNames = parseStudentNames(rawStudentNames);
  const studentNames = Array.isArray(parsedStudentNames)
    ? parsedStudentNames.join("\n")
    : parsedStudentNames;

  return {
    studentNames: studentNames.trim(),
    fromClassName: String(
      prefill?.fromClassName ||
      prefill?.from_class_name ||
      prefill?.className ||
      prefill?.class_name ||
      ""
    ).trim(),
    toClassName: String(
      prefill?.toClassName ||
      prefill?.to_class_name ||
      prefill?.newClassName ||
      prefill?.new_class_name ||
      ""
    ).trim()
  };
};

const splitChildNames = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((name) => name.trim().replace(/\s+/g, " "))
    .filter(Boolean);

const findSingleExactClass = (classes, className) => {
  const target = normaliseText(className);

  if (!target) {
    return null;
  }

  const matches = classes.filter((classItem) => normaliseText(classItem.name) === target);
  return matches.length === 1 ? matches[0] : null;
};

const uniqueStudentsById = (students) => {
  const seenIds = new Set();

  return students.filter((student) => {
    if (seenIds.has(student.id)) {
      return false;
    }

    seenIds.add(student.id);
    return true;
  });
};

function TeacherMoveChildrenModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  onAssignScreener = null,
  onAddMissingChild = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [fromClassSearchTerm, setFromClassSearchTerm] = useState("");
  const [toClassSearchTerm, setToClassSearchTerm] = useState("");
  const [childNamesText, setChildNamesText] = useState("");
  const [selectedFromClass, setSelectedFromClass] = useState(null);
  const [selectedToClass, setSelectedToClass] = useState(null);
  const [fromClassStudents, setFromClassStudents] = useState([]);
  const [studentsLoadedForClassId, setStudentsLoadedForClassId] = useState("");
  const [validChildren, setValidChildren] = useState([]);
  const [missingNames, setMissingNames] = useState([]);
  const [destinationIssues, setDestinationIssues] = useState([]);
  const [movedChildren, setMovedChildren] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const childNames = useMemo(() => splitChildNames(childNamesText), [childNamesText]);
  const filteredFromClasses = useMemo(() => {
    const query = normaliseText(fromClassSearchTerm);
    if (!query) return [];
    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [classes, fromClassSearchTerm]);
  const filteredToClasses = useMemo(() => {
    const query = normaliseText(toClassSearchTerm);
    if (!query) return [];
    return classes.filter((classItem) =>
      classItem.id !== selectedFromClass?.id &&
      [classItem.name, classItem.grade, classItem.academicYear].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [classes, selectedFromClass, toClassSearchTerm]);
  const readyToCheck = Boolean(
    childNames.length &&
    selectedFromClass &&
    selectedToClass &&
    !isLoadingStudents &&
    studentsLoadedForClassId === selectedFromClass.id
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setClasses([]);
    setFromClassSearchTerm(prefillContext.fromClassName);
    setToClassSearchTerm(prefillContext.toClassName);
    setChildNamesText(prefillContext.studentNames);
    setSelectedFromClass(null);
    setSelectedToClass(null);
    setFromClassStudents([]);
    setStudentsLoadedForClassId("");
    setValidChildren([]);
    setMissingNames([]);
    setDestinationIssues([]);
    setMovedChildren([]);
    setError("");

    if (!schoolid) {
      setError("Classes could not be loaded. Please try again.");
      return;
    }

    setIsLoadingClasses(true);

    getClassesBySchoolId(schoolid)
      .then((classesResponse) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .filter((classItem) => classItem.id && classItem.active !== false)
          .filter((classItem) => !classItem.snapshot && !classItem.trial_class)
          .sort((classA, classB) => classA.name.localeCompare(classB.name));

        setClasses(normalisedClasses);
        setSelectedFromClass(findSingleExactClass(normalisedClasses, prefillContext.fromClassName));
        setSelectedToClass(findSingleExactClass(normalisedClasses, prefillContext.toClassName));
      })
      .catch((loadError) => {
        console.error("Error loading classes for move children:", loadError);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, schoolid, prefillContext.studentNames, prefillContext.fromClassName, prefillContext.toClassName]);

  useEffect(() => {
    if (!show || !selectedFromClass || !schoolid) {
      setFromClassStudents([]);
      setStudentsLoadedForClassId("");
      return;
    }

    setIsLoadingStudents(true);
    setStudentsLoadedForClassId("");
    getStudentsByClassId(schoolid, selectedFromClass.id)
      .then((studentsResponse) => {
        const normalisedStudents = (Array.isArray(studentsResponse) ? studentsResponse : [])
          .map((student) => normaliseStudent(student, selectedFromClass))
          .filter((student) => student.id);
        setFromClassStudents(normalisedStudents);
        setStudentsLoadedForClassId(selectedFromClass.id);
      })
      .catch((studentsError) => {
        console.error("Error loading children for move:", studentsError);
        setFromClassStudents([]);
        setError("Children in the current class could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingStudents(false);
      });
  }, [show, schoolid, selectedFromClass]);

  const resolveChildren = () => {
    const studentsByName = new Map();
    fromClassStudents.forEach((student) => {
      const key = normaliseText(student.name);
      studentsByName.set(key, [...(studentsByName.get(key) || []), student]);
    });

    const missing = [];
    const matched = [];

    childNames.forEach((name) => {
      const matches = studentsByName.get(normaliseText(name)) || [];

      if (matches.length === 1) {
        matched.push(matches[0]);
      } else {
        missing.push(name);
      }
    });

    const uniqueMatched = uniqueStudentsById(matched);
    const issues = uniqueMatched.filter((student) => student.classId === selectedToClass?.id);

    return {
      matched: uniqueMatched,
      missing,
      issues
    };
  };

  const moveChildren = async (childrenToMove) => {
    if (!childrenToMove.length || !selectedToClass) {
      return;
    }

    setIsMoving(true);
    setError("");

    try {
      await bulkUpdateStudentClassId({
        studentIds: childrenToMove.map((child) => child.id),
        classId: selectedToClass.id
      });
      setMovedChildren(childrenToMove);
      setStage("success");
    } catch (moveError) {
      console.error("Error moving children:", moveError);
      setError("The children could not be moved. Please try again.");
    } finally {
      setIsMoving(false);
    }
  };

  const handleMoveClick = async () => {
    if (!readyToCheck || isMoving) {
      return;
    }

    const resolved = resolveChildren();
    setValidChildren(resolved.matched);
    setMissingNames(resolved.missing);
    setDestinationIssues(resolved.issues);

    if (resolved.missing.length > 0) {
      setStage("missing");
      return;
    }

    if (resolved.issues.length > 0) {
      setStage("destination");
      return;
    }

    await moveChildren(resolved.matched);
  };

  const removeMissingName = () => {
    const [nameToRemove] = missingNames;
    setChildNamesText(childNames.filter((name) => name !== nameToRemove).join("\n"));
    setMissingNames([]);
    setStage("form");
  };

  const addMissingChild = () => {
    const [missingName] = missingNames;

    if (onAddMissingChild && missingName && selectedFromClass) {
      onAddMissingChild({
        studentName: missingName,
        classItem: selectedFromClass
      });
      return;
    }

    setStage("form");
  };

  const keepIssueChildrenWhereTheyAre = () => {
    const issueIds = new Set(destinationIssues.map((child) => child.id));
    const childrenToMove = validChildren.filter((child) => !issueIds.has(child.id));
    setDestinationIssues([]);

    if (childrenToMove.length > 0) {
      moveChildren(childrenToMove);
    } else {
      setError("No children are ready to move.");
      setStage("form");
    }
  };

  const resetForMore = () => {
    setStage("form");
    setChildNamesText("");
    setFromClassSearchTerm("");
    setToClassSearchTerm("");
    setSelectedFromClass(null);
    setSelectedToClass(null);
    setFromClassStudents([]);
    setStudentsLoadedForClassId("");
    setValidChildren([]);
    setMissingNames([]);
    setDestinationIssues([]);
    setMovedChildren([]);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: "Children moved."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = ({ type, matches, selectedClass, onSelect, loading }) => {
    if (loading) {
      return (
        <div className="move-children-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          key={`${type}-${selectedClass.id}`}
          type="button"
          className="move-children-class-result selected"
          onClick={() => onSelect(null)}
          aria-label={`Select ${selectedClass.name}`}
          aria-pressed
        >
          <span className="move-children-class-copy">
            <strong>{selectedClass.name}</strong>
            <span>{selectedClass.grade || selectedClass.academicYear || "Class details unavailable"}</span>
          </span>
          <span className="move-children-selected-label">Selected</span>
        </button>
      );
    }

    if (matches.length < 1) {
      return <p className="move-children-muted">Start typing the class name.</p>;
    }

    return matches.map((classItem) => {
      const selected = selectedClass?.id === classItem.id;

      return (
        <button
          key={`${type}-${classItem.id}`}
          type="button"
          className={`move-children-class-result${selected ? " selected" : ""}`}
          onClick={() => onSelect(classItem)}
          aria-label={`Select ${classItem.name}`}
          aria-pressed={selected}
        >
          <span className="move-children-class-copy">
            <strong>{classItem.name}</strong>
            <span>{classItem.grade || classItem.academicYear || "Class details unavailable"}</span>
          </span>
          {selected && <span className="move-children-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Move Children</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="move-children-intro">Move children to another class without losing their records or results.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="move-children-grid">
          <Form.Group controlId="teacherMoveChildrenNames">
            <Form.Label>Which children should be moved?</Form.Label>
            <Form.Text as="div" className="move-children-field-label">
              Child names
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={6}
              value={childNamesText}
              onChange={(event) => setChildNamesText(event.target.value)}
              aria-label="Child names to move"
            />
            <Form.Text className="text-muted">One child per line.</Form.Text>
          </Form.Group>

          <div>
            <Form.Group controlId="teacherMoveChildrenFromClass">
              <Form.Label>Move from</Form.Label>
              <Form.Text as="div" className="move-children-field-label">
                Current class
              </Form.Text>
              <Form.Control
                type="search"
                value={fromClassSearchTerm}
                onChange={(event) => {
                  setFromClassSearchTerm(event.target.value);
                  setSelectedFromClass(null);
                  setSelectedToClass(null);
                  setStudentsLoadedForClassId("");
                  setError("");
                }}
                aria-label="Search current class"
              />
            </Form.Group>
            <div className="move-children-results">
              {renderClassResults({
                type: "from",
                matches: filteredFromClasses,
                selectedClass: selectedFromClass,
                onSelect: (classItem) => {
                  setSelectedFromClass(classItem);
                  setSelectedToClass(null);
                  setFromClassSearchTerm(classItem?.name || fromClassSearchTerm);
                  setStudentsLoadedForClassId("");
                },
                loading: isLoadingClasses
              })}
            </div>
          </div>

          <div>
            <Form.Group controlId="teacherMoveChildrenToClass">
              <Form.Label>Move to</Form.Label>
              <Form.Text as="div" className="move-children-field-label">
                New class
              </Form.Text>
              <Form.Control
                type="search"
                value={toClassSearchTerm}
                onChange={(event) => {
                  setToClassSearchTerm(event.target.value);
                  setSelectedToClass(null);
                  setError("");
                }}
                aria-label="Search new class"
                disabled={!selectedFromClass}
              />
            </Form.Group>
            <div className="move-children-results">
              {selectedFromClass
                ? renderClassResults({
                  type: "to",
                  matches: filteredToClasses,
                  selectedClass: selectedToClass,
                  onSelect: (classItem) => {
                    setSelectedToClass(classItem);
                    setToClassSearchTerm(classItem?.name || toClassSearchTerm);
                  },
                  loading: isLoadingClasses
                })
                : <p className="move-children-muted">Choose the current class first.</p>}
            </div>
          </div>
        </div>
        <div className="move-children-note">
          <strong>{childNames.length} children found.</strong>
          <span>Records, results, support details, and assigned screeners stay with each child.</span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="move-children-footer-note">
          {selectedFromClass && selectedToClass
            ? `${selectedFromClass.name} to ${selectedToClass.name}.`
            : "Choose children and both classes."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleMoveClick} disabled={!readyToCheck || isMoving}>
          {isMoving ? "Moving..." : "Move children"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderMissing = () => {
    const missingName = missingNames[0];

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Child Not Found</Modal.Title>
        </Modal.Header>
        <Modal.Body className="move-children-missing-body">
          <p className="move-children-intro">One name is not in {selectedFromClass?.name}.</p>
          <div className="move-children-note">
            <strong>{validChildren.length} children can move.</strong>
            <span>{missingName} is not in {selectedFromClass?.name}.</span>
          </div>
          <Alert variant="warning" className="move-children-warning">
            <FaExclamationCircle aria-hidden="true" />
            <span>
              <strong>We could not find {missingName} in {selectedFromClass?.name}.</strong>
              <span>If this child is in this school, add them to {selectedFromClass?.name} first. If the name is wrong, edit or remove it.</span>
            </span>
          </Alert>
          <div className="move-children-attention-actions">
            <Button variant="primary" onClick={addMissingChild}>
              Add {missingName} to {selectedFromClass?.name}
            </Button>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>
              Edit names
            </Button>
            <Button variant="outline-secondary" onClick={removeMissingName}>
              Remove this name
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer className="move-children-missing-footer">
          <span className="move-children-footer-note">Choose what to do before continuing.</span>
          <Button variant="outline-secondary" onClick={() => setStage("form")}>
            Back
          </Button>
          <Button variant="primary" disabled>
            Continue
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderDestinationIssue = () => {
    const issueChild = destinationIssues[0];
    const readyChildren = validChildren.filter(
      (child) => !destinationIssues.some((issue) => issue.id === child.id)
    );

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Check Before Moving</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="move-children-intro">One child is already in the class you selected.</p>
          <div className="move-children-note">
            <strong>{readyChildren.length} children can move now.</strong>
            <span>{issueChild?.name} is already in {selectedToClass?.name}.</span>
          </div>
          <Alert variant="warning" className="move-children-warning">
            <FaExclamationCircle aria-hidden="true" />
            <span>
              <strong>{issueChild?.name} may already be in {selectedToClass?.name}.</strong>
              <span>Keep this child where they are, or move the other {readyChildren.length} children now.</span>
            </span>
          </Alert>
          <div className="move-children-attention-actions">
            <Button variant="outline-secondary" onClick={keepIssueChildrenWhereTheyAre}>
              Keep {issueChild?.name} where they are
            </Button>
            <Button variant="primary" onClick={keepIssueChildrenWhereTheyAre} disabled={readyChildren.length < 1}>
              Move other {readyChildren.length} children
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="move-children-footer-note">{readyChildren.length} children ready.</span>
          <Button variant="outline-secondary" onClick={() => setStage("form")}>
            Back
          </Button>
          <Button variant="primary" onClick={keepIssueChildrenWhereTheyAre} disabled={readyChildren.length < 1 || isMoving}>
            {isMoving ? "Moving..." : "Move children"}
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Children Moved</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="move-children-intro">Class lists have been updated.</p>
        <Alert variant="success" className="move-children-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{movedChildren.length} children have been moved to {selectedToClass?.name}.</strong>
            <span>They no longer appear in {selectedFromClass?.name}. Their records, results, support details, and assigned screeners were kept.</span>
          </span>
        </Alert>
        <div className="move-children-summary">
          <strong>What happened</strong>
          <span className="move-children-row">
            <span>Moved</span>
            <span>{movedChildren.map((child) => child.name).join(", ")}</span>
          </span>
          <span className="move-children-row">
            <span>From</span>
            <span>{selectedFromClass?.name}</span>
          </span>
          <span className="move-children-row">
            <span>To</span>
            <span>{selectedToClass?.name}</span>
          </span>
          <span className="move-children-row">
            <span>Kept</span>
            <span>Child records, support details, assessment results, and assigned screeners.</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="move-children-footer-note">{embedded ? "Returns to SENSAI." : `${selectedToClass?.name} updated.`}</span>
        <Button variant="outline-secondary" onClick={resetForMore}>
          Move more children
        </Button>
        {onAssignScreener && (
          <Button
            variant="outline-secondary"
            onClick={() => onAssignScreener({ className: selectedToClass?.name })}
          >
            Assign screener
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  const renderStage = () => {
    if (stage === "missing") return renderMissing();
    if (stage === "destination") return renderDestinationIssue();
    if (stage === "success") return renderSuccess();
    return renderForm();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-move-children-modal"
    >
      {renderStage()}
    </Modal>
  );
}

export default TeacherMoveChildrenModal;
